import { Component, type ErrorInfo, type ReactNode } from "react";
import { AlertTriangle, RefreshCcw } from "lucide-react";
import { Button } from "@/components/ui/button";

interface Props {
  children: ReactNode;
  /** Optional fallback override. Receives the error and a reset callback. */
  fallback?: (error: Error, reset: () => void) => ReactNode;
  /** When true, render nothing on error (useful for non-critical widgets). */
  silent?: boolean;
  /** Identifier for logs/analytics. */
  name?: string;
}

interface State {
  error: Error | null;
}

class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // Lightweight client-side reporting hook. Replace with Sentry/Logtail later.
    if (typeof window !== "undefined") {
      // eslint-disable-next-line no-console
      console.error(`[ErrorBoundary:${this.props.name ?? "root"}]`, error, info.componentStack);
    }
  }

  reset = () => this.setState({ error: null });

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;

    if (this.props.silent) return null;

    if (this.props.fallback) return this.props.fallback(error, this.reset);

    return (
      <div
        role="alert"
        className="min-h-[200px] flex items-center justify-center p-6"
      >
        <div className="max-w-md text-center bg-card border rounded-xl p-6 shadow-sm">
          <div className="mx-auto w-12 h-12 rounded-full bg-destructive/10 text-destructive flex items-center justify-center mb-4">
            <AlertTriangle className="w-6 h-6" aria-hidden="true" />
          </div>
          <h2 className="text-lg font-semibold mb-1">Something went wrong</h2>
          <p className="text-sm text-muted-foreground mb-4">
            This section couldn’t load. The rest of the page is still usable.
          </p>
          <Button onClick={this.reset} size="sm" variant="outline">
            <RefreshCcw className="w-4 h-4 mr-2" />
            Try again
          </Button>
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;
